import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useLanguage } from "../context/LanguageContext";
import PublicNav from "../components/PublicNav";
import LanguageSelector from "../components/LanguageSelector";
import LegalEntityDisclosure from "../components/LegalEntityDisclosure";
import api from "../lib/api";
import { DISPLAY_DOMAIN } from "../lib/brand";
import {
  ArrowRight, 
  BadgeCheck,
  FileText,
  Shield, 
  Lock, 
  TrendingUp,
  Users,
  CheckCircle2,
  Landmark,
  Award,
  Globe,
  Building2,
  Scale,
  ChevronDown,
  Briefcase,
  PieChart,
  BarChart3,
  Gem,
  CircleDollarSign,
  Calendar,
  XCircle,
  Phone,
  Mail
} from "lucide-react";

const AboutPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    clientAssets: 280000000,
    totalPartners: 0,
    totalClients: 0
  });
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    // Fetch dynamic stats
    const fetchStats = async () => {
      try {
        // Try to get user counts if admin endpoint is accessible
        const response = await api.get("/users/count");
        if (response.data) {
          const partners = response.data.referrers || 0;
          const clients = response.data.clients || 0;
          // Client assets grow by $500K per partner and $100K per client
          const dynamicAssets = 280000000 + (partners * 500000) + (clients * 100000);
          setStats({
            clientAssets: dynamicAssets,
            totalPartners: partners,
            totalClients: clients
          });
        }
      } catch (error) {
        // Use default values if not accessible
        setStats({
          clientAssets: 280000000,
          totalPartners: 12,
          totalClients: 45
        });
      }
    };
    fetchStats();
  }, []);

  const formatCurrency = (amount) => {
    if (amount >= 1000000000) {
      return `$${(amount / 1000000000).toFixed(1)}B+`;
    } else if (amount >= 1000000) {
      return `$${Math.floor(amount / 1000000)}M+`;
    }
    return `$${amount.toLocaleString()}`;
  };

  // STARSEED-CAPMARKETS-088 — six principles supplied verbatim by Jeff
  // (2026-07-27 stage review). Wording is partner-approved compliance copy:
  // edit only with his sign-off.
  const values = [
    {
      icon: Users,
      title: "Relationships First",
      description: "We believe meaningful access begins with trusted relationships, shared knowledge, and a community of accomplished professionals."
    },
    {
      icon: Shield,
      title: "Compliance-Conscious Access",
      description: "Our membership and introduction processes are structured to maintain a clear boundary between EchoSense and the independent financial and investment providers accessible through the Club."
    },
    {
      icon: BadgeCheck,
      title: "Qualified Membership",
      description: "Applicants complete the Club's applicable identity, eligibility, and accreditation-verification process before receiving access to restricted areas of the member portal."
    },
    {
      icon: Lock,
      title: "Informed Member Choice",
      description: "Members independently decide whether they wish to engage with an opportunity through our independent partner. Member information is transferred only after documented consent has been provided."
    },
    {
      icon: Scale,
      title: "Clear Role Separation",
      description: "EchoSense facilitates relationships, education, and introductions. Each independent partner remains responsible for its own offering materials, disclosures, due diligence, eligibility and suitability determinations, account approvals, financial arrangements, custody disclosures, tax reporting, and ongoing reporting."
    },
    {
      icon: FileText,
      title: "Transparency and Documentation",
      description: "Important communications, disclosures, permissions, and introductions are clearly documented and responsibly maintained."
    }
  ];

  // Leadership bios supplied verbatim by Jeff (2026-07-27 stage review); Kevin's
  // is condensed from his own draft to match the others' length (089 holds the
  // full text). Order is CEO → CTO → Directors.
  // NOTE: Jeff's email spelled the surname both "Reigherd" (heading) and
  // "Reighard" (bio body, twice). Using the body spelling pending confirmation
  // before this reaches prod.
  const leadership = [
    {
      name: "Jeff D. Jenson",
      title: "Chief Executive Officer",
      bio: "Jeff D. Jenson is a seasoned entrepreneur, investment banker, and business strategist with over 25 years of experience building, scaling, and capitalizing companies across multiple industries. He has played a key role in taking nearly 70 companies public and raising over $2 billion in capital, with notable involvement in companies such as Northern Oil & Gas and Riley Exploration Permian. Jeff is the founder and CEO of Tryant AI Solutions, where he focuses on leveraging technology to scale business performance. Known for his ability to identify opportunity, structure complex deals, and execute at a high level, Jeff bridges traditional finance, real estate, and emerging technology to build scalable, high-impact businesses."
    },
    {
      name: "Kevin Stuart",
      title: "Chief Technology Officer",
      bio: "Kevin Stuart is the Chief Technology Officer of EchoSense Capital Club, where he leads the technology strategy behind the Club and the platform that powers it. With more than 20 years of experience in DevOps, cloud infrastructure, platform engineering, and enterprise systems, Kevin has built and operated secure, scalable technology across complex production environments — spanning cloud architecture, infrastructure automation, distributed systems, observability, and data governance. He is also a founder and technical architect at Tryant AI Solutions, where he helped create an AI-native platform designed to organize knowledge, preserve context, and support evidence-based decision-making. At the Club, Kevin's focus is technology that is secure, resilient, and auditable — so members can understand not only what a system concludes, but why."
    },
    {
      name: "John Reighard",
      title: "Director of Operations",
      bio: "John Reighard is an entrepreneur, investor, and capital strategist with a strong focus on connecting high-growth companies with qualified investors through the Angel Investors Network. With extensive experience in fundraising, deal structuring, and investor relations, he has helped facilitate capital raises across a wide range of industries, supporting startups and emerging companies in scaling their operations. John is known for his ability to bridge entrepreneurs and capital, creating opportunities for both founders and investors while fostering long-term strategic partnerships."
    },
    {
      name: "George Veronis",
      title: "Director of Sales",
      bio: "George Veronis is a seasoned entrepreneur and direct sales expert with more than three decades of experience in sales leadership, management, and team development. Driven by a deep passion for entrepreneurship, he has built a career centered on mastering the art of sales and empowering others to do the same. George specializes in sales coaching and strategic recruitment, helping individuals and organizations unlock growth through proven systems, the right tools, and a performance-driven mindset. Known for his belief that sales is a learnable skill, not an innate talent, he has mentored countless professionals to achieve both personal and financial success by developing confidence, discipline, and leadership within high-performing teams."
    }
  ];

  const milestones = [
    { year: "2024", event: "EchoSense Capital Club founded as a private VIP Capital Club member portal" },
    { year: "2025", event: "Market opportunity identified — most trading platforms are not designed to give affluent qualified retail investors access to the full range of opportunities available in the global market" },
    { year: "2025", event: "Compliant introducer network and referral compliance framework built" },
    { year: "2025", event: "Verification portal expanded with KYC/AML and Accredited Investor review" },
    { year: "2026", event: "Approved US Regulated Partner Onboarding Pathway integrated" }
  ];

  const investmentOpportunities = [
    {
      icon: Briefcase,
      title: "Verification Portal",
      description: "Private intake, identity verification, and compliance review for prospective members, introducers, accredited investors, and family offices.",
      features: ["KYC / AML", "Sanctions screening", "Source-of-funds review"]
    },
    {
      icon: PieChart,
      title: "Accredited / Qualified Review",
      description: "Eligibility review under U.S. Reg D / Reg S or non-U.S. equivalents. EchoSense documents eligibility — the regulated partner makes any final determination.",
      features: ["Reg D / Reg S", "Non-U.S. professional", "Documentation only"]
    },
    {
      icon: BarChart3,
      title: "Data Transfer Consent",
      description: "Three-checkbox written consent authorizing transfer of your verification data to the Approved Regulated Partner Onboarding Pathway.",
      features: ["Typed signature", "Audit-grade receipt", "Versioned consent"]
    },
    {
      icon: Gem,
      title: "Regulated Partner Pathway",
      description: "Once your file is forwarded, the regulated partner contacts you directly with onboarding forms, login credentials, and any written funding instructions.",
      features: ["Partner-led onboarding", "Direct credentials", "Written instructions only"]
    }
  ];

  const faqs = [
    {
      question: "What is EchoSense Capital Club?",
      answer: "EchoSense Capital Club is a private member, introducer, and verification portal. We do not receive client funds, provide investment advice, manage trading accounts, or approve investor participation. We help qualified prospects complete intake, KYC/AML, and accredited or qualified investor verification before forwarding their file — with written consent — to the Approved Regulated Partner Onboarding Pathway."
    },
    {
      question: "How is investor information protected?",
      answer: "We collect only the information needed to complete identity verification, AML screening, and accredited or qualified investor review. Your data is transmitted to the regulated onboarding partner only after you provide a written, three-checkbox data-transfer consent that includes a typed signature, IP and timestamp."
    },
    {
      question: "What returns can I expect from EchoSense?",
      answer: "EchoSense Capital Club does not guarantee, project, or quote any return, allocation, or trading result. We are an introducer and verification portal — investment-related documents, fund terms, and any performance information are provided directly by the regulated partner, fund administrator, or other approved third party once you engage with them directly."
    },
    {
      question: "What is the registration / membership fee?",
      answer: "EchoSense charges a $999 one-time registration fee and a $99 monthly membership fee for portal access, verification handling, and introducer-network participation. These fees are unrelated to any investment, deposit, or onboarding capital handled directly by the regulated partner."
    },
    {
      question: "How does the introducer program work?",
      answer: "Our two-tier introducer program tracks who refers a client into the verification portal and what consents were captured. Introducers may receive permitted referral compensation only as allowed under the final written structure approved by the regulated partner."
    },
    {
      question: "Where do I send funds?",
      answer: "Do not send funds to EchoSense, any introducer, any individual, or any unapproved account. You will only follow written funding instructions issued directly by the approved regulated onboarding partner or fund administrator after they have reviewed and approved your file."
    },
    {
      question: "Is participation guaranteed?",
      answer: "No. Submission of your intake does not guarantee acceptance, allocation, account approval, investment participation, compensation eligibility, trading access, funding approval, or any return. Final approval rests with the regulated partner."
    },
    {
      question: "How do I get started?",
      answer: "Click 'Start Investor Qualification' on the home page to complete the intake, then KYC/AML, accredited or qualified investor review, and finally the data-transfer consent. Once approved, your file is forwarded to the Approved Regulated Partner Onboarding Pathway who will contact you directly with next steps."
    },
    {
      question: "Who is the regulated partner?",
      answer: "The regulated partner's identity will be disclosed to you directly during the partner onboarding step, after you have completed verification and provided written data-transfer consent. EchoSense does not publicly disclose partner names until company information is approved for disclosure."
    }
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      <PublicNav active="about" />

      {/* Hero Section */}
      <section className="py-24 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-burgundy uppercase tracking-[0.3em] text-sm mb-4">{t('nav.about')}</p>
            <h1 className="text-5xl md:text-6xl font-semibold text-platinum font-serif leading-tight">
              {t('about.title')}<br />
              <span className="gradient-platinum">{t('about.subtitle')}</span>
            </h1>
            <p className="mt-8 text-xl text-platinum/70 leading-relaxed">
              {t('about.section1_content')}
            </p>
          </div>
        </div>
      </section>

      {/* What EchoSense Capital Club Does NOT DO! */}
      <section className="py-16 px-8 bg-red-900/10 border-y border-red-500/20" data-testid="about-not-do-section">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-sm bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <XCircle className="text-red-500" size={24} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-platinum font-serif text-center" data-testid="about-not-do-title">
              {t('about.section2_title')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {t('about.section2_items').map((item, idx) => (
              <div
                key={`about-not-do-${item}-${idx}`}
                className="flex flex-col items-center justify-center gap-2 p-4 rounded-md bg-white/60 border border-red-200"
                data-testid={`about-not-do-item-${idx}`}
              >
                <XCircle size={18} className="text-red-500" />
                <span className="text-sm font-bold text-platinum text-center">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-slate-600 mt-6 text-sm italic text-center" data-testid="about-not-do-note">{t('about.section2_note')}</p>
        </div>
      </section>

      {/* Live Stats Section */}
      <section className="py-16 px-8 bg-surface-highlight border-y border-platinum/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-serif font-semibold text-platinum">
                Private
              </p>
              <p className="text-platinum/50 text-sm mt-2 uppercase tracking-wider">Member Portal</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-serif font-semibold text-platinum">KYC + AML</p>
              <p className="text-platinum/50 text-sm mt-2 uppercase tracking-wider">Verification</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-serif font-semibold text-platinum">Regulated</p>
              <p className="text-platinum/50 text-sm mt-2 uppercase tracking-wider">Partner Pathway</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-serif font-semibold text-platinum">Introducer</p>
              <p className="text-platinum/50 text-sm mt-2 uppercase tracking-wider">Network Only</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 px-8 bg-obsidian">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12 items-start">
            <div className="lg:col-span-2">
              <p className="text-burgundy uppercase tracking-[0.2em] text-sm mb-4">Our Story</p>
              <h2 className="text-4xl font-semibold text-platinum font-serif mb-6">
                Relationships Create Access
              </h2>
              {/* Copy supplied verbatim by Jeff (2026-07-27 stage review) —
                  partner-approved compliance wording; edit only with sign-off. */}
              <div className="space-y-4 text-platinum/70 leading-relaxed">
                <p>
                  EchoSense Capital Club was founded on a simple belief: qualified individuals
                  should have a trusted pathway to relationships, education, and opportunities
                  typically reserved for institutions.
                </p>
                <p>
                  We created a private membership community where successful professionals,
                  entrepreneurs, and accredited investors can connect, collaborate, and engage
                  with carefully selected independent partners. Our process begins with member
                  qualification, identity verification, and clear documentation—creating a
                  professional environment built around trust, transparency, and responsible
                  access.
                </p>
                <p>
                  EchoSense serves as the relationship and access layer. We cultivate the
                  community, coordinate education and events, facilitate introductions, and
                  provide a secure portal through which qualified members can explore curated
                  opportunities. When a member chooses to engage with an opportunity, the
                  applicable independent partner assumes responsibility for its own due
                  diligence, disclosures, eligibility and suitability determinations, account
                  approvals, documentation, funding instructions, and reporting. The independent
                  partner also establishes or discloses the applicable custody arrangements.
                </p>
                <p>
                  EchoSense does not provide investment advice, recommend investments, determine
                  suitability, approve accounts, manage trading activity, receive or hold client
                  funds, provide funding instructions, issue tax forms, or guarantee results.
                </p>
                <p>
                  Today, EchoSense is building a growing community of qualified individuals who
                  understand that exceptional opportunities often begin with exceptional
                  relationships.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-burgundy/10 to-transparent rounded-lg"></div>
              <Card className="bg-surface border-platinum/15 relative">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <Building2 className="text-platinum" size={32} />
                    <div>
                      <h3 className="text-platinum font-serif text-xl font-semibold" data-testid="about-capital-club">EchoSense Capital Club</h3>
                      <p className="text-slate-600 text-sm">Established 2024</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="text-emerald" size={18} />
                      <span className="text-platinum/80">Members-only capital club</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="text-emerald" size={18} />
                      <span className="text-platinum/80">Documentation and disclosure first</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="text-emerald" size={18} />
                      <span className="text-platinum/80">Clear separation between introducer and regulated partner</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="text-emerald" size={18} />
                      <span className="text-platinum/80">Verification before participation</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-8 bg-surface-highlight/60">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-burgundy uppercase tracking-[0.2em] text-sm mb-4">Our Values</p>
            <h2 className="text-4xl font-semibold text-platinum font-serif">
              The Principles That Guide Us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card 
                key={value.title}
                className="bg-surface border-platinum/10 card-hover"
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-sm bg-burgundy/20 flex items-center justify-center flex-shrink-0">
                      <value.icon className="text-burgundy" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-platinum mb-2 font-serif">{value.title}</h3>
                      <p className="text-platinum/60 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership — STARSEED-CAPMARKETS-088/089 */}
      <section className="py-24 px-8 bg-obsidian" data-testid="about-leadership">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-burgundy uppercase tracking-[0.2em] text-sm mb-4">Leadership</p>
            <h2 className="text-4xl font-semibold text-platinum font-serif">
              The Team Behind the Club
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {leadership.map((person) => (
              <Card
                key={person.name}
                className="bg-surface border-platinum/15 card-hover"
                data-testid={`leader-${person.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold text-cream font-serif">{person.name}</h3>
                  <p className="text-gold text-[11px] uppercase tracking-[0.25em] font-semibold mt-1 mb-4">
                    {person.title}
                  </p>
                  <p className="text-platinum/70 text-sm leading-relaxed">{person.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* EchoSense Capital Club Partnership */}
      <section className="py-24 px-8 bg-obsidian">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <Card className="bg-surface border-platinum/15">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <Landmark className="text-platinum" size={40} />
                    <div>
                      <h3 className="text-cream font-serif text-2xl">Approved Regulated Partner</h3>
                      <p className="text-platinum/50">Identity confirmed at onboarding</p>
                    </div>
                  </div>
                  <p className="text-platinum/70 mb-6 leading-relaxed">
                    Once a member completes EchoSense intake, KYC/AML, accredited or qualified
                    investor verification, and the data-transfer consent, their file is forwarded
                    to the approved regulated onboarding partner who handles account approval,
                    funding instructions, reporting, and tax documentation directly.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded bg-obsidian border border-platinum/10">
                      <p className="text-2xl font-serif font-semibold text-platinum">Verified</p>
                      <p className="text-platinum/50 text-sm">Compliance pathway</p>
                    </div>
                    <div className="p-4 rounded bg-obsidian border border-platinum/10">
                      <p className="text-2xl font-serif font-semibold text-platinum">Direct</p>
                      <p className="text-platinum/50 text-sm">Partner-led onboarding</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-burgundy uppercase tracking-[0.2em] text-sm mb-4">Partnership</p>
              <h2 className="text-4xl font-semibold text-platinum font-serif mb-6">
                Compliance Before Connection
              </h2>
              <p className="text-platinum/70 mb-6 leading-relaxed">
                EchoSense Capital Club is the verification and relationship layer. Our role is to
                qualify, document, and — with written consent — forward your file to the regulated
                onboarding partner. The regulated partner is the licensed entity that owns the
                account relationship from there.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-platinum/80">
                  <CheckCircle2 className="text-emerald" size={18} />
                  <span>Identity, KYC, and AML verification</span>
                </div>
                <div className="flex items-center gap-3 text-platinum/80">
                  <CheckCircle2 className="text-emerald" size={18} />
                  <span>Accredited / qualified investor review</span>
                </div>
                <div className="flex items-center gap-3 text-platinum/80">
                  <CheckCircle2 className="text-emerald" size={18} />
                  <span>Written, audit-grade consent receipts</span>
                </div>
                <div className="flex items-center gap-3 text-platinum/80">
                  <CheckCircle2 className="text-emerald" size={18} />
                  <span>Documented handoff to regulated partner</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 px-8 bg-surface-highlight/60">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-burgundy uppercase tracking-[0.2em] text-sm mb-4">Our Journey</p>
            <h2 className="text-4xl font-semibold text-platinum font-serif">
              Key Milestones
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-px bg-platinum/20"></div>
            <div className="space-y-8">
              {milestones.map((milestone) => (
                <div key={`milestone-${milestone.year}`} className="relative flex items-start gap-8 pl-8">
                  <div className="absolute left-6 w-4 h-4 rounded-full bg-burgundy border-2 border-navy-light"></div>
                  <div className="flex-shrink-0 w-16">
                    <span className="text-platinum font-serif font-semibold">{milestone.year}</span>
                  </div>
                  <div className="flex-1 pb-8">
                    <p className="text-platinum/80">{milestone.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Investment Opportunities Section */}
      <section className="py-24 px-8 bg-obsidian">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-burgundy uppercase tracking-[0.2em] text-sm mb-4">Verification & Pathway</p>
            <h2 className="text-4xl md:text-5xl font-semibold text-platinum font-serif mb-4">
              The EchoSense <span className="gradient-platinum">Pathway</span>
            </h2>
            <p className="text-lg text-platinum/60 max-w-3xl mx-auto">
              A documented, compliance-first journey from intake to the Approved Regulated Partner
              Onboarding Pathway. EchoSense does not provide investment advice or hold client funds.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {investmentOpportunities.map((opportunity, index) => (
              <Card 
                key={opportunity.title}
                className="bg-surface border-platinum/15 card-hover overflow-hidden"
              >
                <CardContent className="p-0">
                  <div className="p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 rounded-sm bg-burgundy/20 flex items-center justify-center flex-shrink-0">
                        <opportunity.icon className="text-burgundy" size={28} />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-platinum font-serif">{opportunity.title}</h3>
                      </div>
                    </div>
                    <p className="text-platinum/60 leading-relaxed mb-6">{opportunity.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {opportunity.features.map((feature, idx) => (
                        <span 
                          key={`${opportunity.title}-feat-${idx}-${feature}`}
                          className="px-3 py-1 rounded-full text-xs bg-platinum/10 text-platinum/80 border border-platinum/20"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="px-8 py-4 bg-surface border-t border-platinum/10">
                    <div className="flex items-center justify-between">
                      <span className="text-platinum/50 text-sm">Pathway</span>
                      <span className="text-emerald font-semibold">Regulated Partner</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 p-6 rounded-lg bg-burgundy/10 border border-burgundy/30">
            <div className="flex items-start gap-4">
              <CircleDollarSign className="text-burgundy flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="text-platinum font-semibold mb-2">Compliance-First Philosophy</h4>
                <p className="text-platinum/80 text-sm leading-relaxed">
                  Every applicant we forward has completed identity verification, AML screening, and
                  accredited or qualified investor review, plus a written data-transfer consent.
                  EchoSense does not approve accounts, hold client funds, or provide investment
                  advice — final approval rests with the regulated partner.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-8 bg-surface-highlight/60">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-burgundy uppercase tracking-[0.2em] text-sm mb-4">{t('nav.faq')}</p>
            <h2 className="text-4xl font-semibold text-platinum font-serif mb-4">
              {t('faq.title')}
            </h2>
            <p className="text-platinum/60">
              {t('faq.subtitle')}
            </p>
          </div>

          <div className="space-y-4">
            {t('faq.questions').map((faq, index) => (
              <div 
                key={`faq-${index}-${faq.q?.slice(0,24)}`}
                className="border border-platinum/10 rounded-lg overflow-hidden bg-surface"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-platinum/5 transition-colors"
                >
                  <span className="text-platinum font-semibold pr-4">{faq.q}</span>
                  <ChevronDown 
                    className={`text-slate-500 flex-shrink-0 transition-transform duration-200 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`} 
                    size={20} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 border-t border-platinum/10">
                    <p className="text-platinum/80 leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-platinum/50 mb-4">{language === 'en' ? 'Still have questions?' : '¿Aún tienes preguntas?'}</p>
            <Button 
              className="bg-gold hover:bg-gold-light text-navy-dark shadow-sm"
              onClick={() => navigate("/consultation")}
            >
              <Calendar className="mr-2" size={18} />
              {t('nav.consultation')}
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-8 bg-obsidian relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-burgundy/10 via-transparent to-burgundy/10"></div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-semibold text-platinum font-serif mb-6">
            Ready to Protect Your Wealth?
          </h2>
          <p className="text-xl text-platinum/60 leading-relaxed mb-8">
            Join a community of sophisticated investors who have chosen security without sacrificing returns.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gold hover:bg-gold-light text-navy-dark shadow-sm font-medium px-8"
              onClick={() => navigate("/register")}
            >
              Begin Your Journey
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-platinum/30 text-platinum hover:bg-platinum/10"
              onClick={() => navigate("/")}
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-8 bg-obsidian border-t border-platinum/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <img src="/logo.png" alt="EchoSense Capital Club" className="w-14 h-14 object-contain" />
                <div>
                  <span className="text-xl font-semibold text-platinum font-serif">EchoSense Capital Club</span>
                  <span className="text-platinum/40 text-sm block">{DISPLAY_DOMAIN}</span>
                </div>
              </div>
              <p className="text-platinum/50 text-sm">
                {language === 'en' 
                  ? 'Introducer management and compliance tracking platform.'
                  : 'Plataforma de gestión de introductores y seguimiento de cumplimiento.'
                }
              </p>
            </div>
            <div>
              <h4 className="text-platinum font-medium mb-4">{language === 'en' ? 'Quick Links' : 'Enlaces Rápidos'}</h4>
              <div className="space-y-2">
                <Link to="/" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">{t('nav.home')}</Link>
                <Link to="/about" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">{t('nav.about')}</Link>
                <Link to="/contact" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">{t('nav.contact')}</Link>
                <Link to="/login" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">{t('nav.login')}</Link>
                <Link to="/register" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">{t('nav.register')}</Link>
              </div>
            </div>
            <div>
              <h4 className="text-platinum font-medium mb-4">{language === 'en' ? 'Contact' : 'Contacto'}</h4>
              <div className="space-y-3">
                <a href="mailto:admin@echosensecapital.com" className="flex items-center gap-2 text-platinum/50 hover:text-platinum transition-colors text-sm">
                  <Mail size={14} />
                  admin@echosensecapital.com
                </a>
                <a href="tel:+17025180208" className="flex items-center gap-2 text-platinum/50 hover:text-platinum transition-colors text-sm">
                  <Phone size={14} />
                  (702) 518-0208
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-platinum font-medium mb-4">{language === 'en' ? 'Language' : 'Idioma'}</h4>
              <LanguageSelector variant="inline" />
            </div>
          </div>
          
          <div className="pt-8 border-t border-platinum/10">
            {/* Legal entity disclosure — required by KYC providers (SumSub) */}
            <div className="mb-4">
              <LegalEntityDisclosure variant="footer" />
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-platinum/40 text-xs">
                © {new Date().getFullYear()} EchoSense Capital Club. {language === 'en' ? 'All rights reserved.' : 'Todos los derechos reservados.'}{' · '}<Link to="/disclaimer" className="text-platinum/40 hover:text-platinum text-xs underline-offset-2 hover:underline" data-testid="footer-disclaimer">{language === 'en' ? 'Disclaimer' : 'Aviso legal'}</Link>
              </p>
              <p className="text-platinum/30 text-xs text-center max-w-2xl">
                <strong>{language === 'en' ? 'Compliance Statement:' : 'Declaración de Cumplimiento:'}</strong> {t('about.conclusion_content')}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
